
import React, { useEffect, useState, useRef, useCallback } from 'react';
import SectionHeading from '../ui/SectionHeading';
import TestimonialItem from '../reviews/TestimonialItem';
import { useReviews } from '@/hooks/useReviews';
import { Skeleton } from '../ui/skeleton';
import { toast } from '@/hooks/use-toast';

type Review = {
  id: string;
  name: string;
  position: string;
  company: string;
  rating: number;
  text: string;
  status: string;
  created_at?: string;
}

const TrustSection: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const testimonialsRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const { fetchPublicReviews } = useReviews();
  const [hasFetched, setHasFetched] = useState(false);

  // Initial hardcoded testimonial to ensure there's always at least one showing
  const initialTestimonials = [
    {
      id: "initial",
      text: "Very helpful navigating the AI process. Professional service which has allowed us to add considerable value to our business marketing.",
      name: "Will Percy",
      position: "CMO",
      company: "Banh Mi Kitchen",
      rating: 5,
      status: "approved"
    }
  ];

  // Use useCallback to prevent the function from being recreated on every render
  const fetchReviews = useCallback(async () => {
    if (hasFetched) return; // Prevent multiple fetches
    
    // Don't show loading state if we already have initial testimonials
    if (reviews.length === 0) {
      setIsLoading(true);
    }
    
    try {
      console.log('Fetching reviews using useReviews hook...');
      // Use anonymous public access to fetch reviews
      const fetchedReviews = await fetchPublicReviews();
      
      console.log('Reviews fetched successfully:', fetchedReviews);
      
      // Combine static testimonials with dynamic reviews from database
      // Always include the initial testimonial
      const combinedReviews = [...initialTestimonials];
      
      if (fetchedReviews && fetchedReviews.length > 0) {
        console.log(`Adding ${fetchedReviews.length} reviews from database`);
        combinedReviews.push(...fetchedReviews);
      } else {
        console.log('No reviews fetched from database or empty array returned');
      }
      
      console.log('Final combined reviews:', combinedReviews.length);
      setReviews(combinedReviews);
      setError(null);
      setHasFetched(true);
    } catch (err) {
      console.error('Exception fetching reviews:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      // Ensure we still show the initial testimonial if there's an error
      setReviews(initialTestimonials);
      setHasFetched(true);
      
      // Show error toast only once
      if (!hasFetched) {
        toast({
          title: "Error loading reviews",
          description: "We'll show you our default review while we fix this.",
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchPublicReviews, hasFetched, reviews.length]);

  useEffect(() => {
    fetchReviews();
    // We're using useCallback for fetchReviews, so it will only change if its dependencies change
  }, [fetchReviews]);

  // Determine layout based on number of reviews
  const getLayoutClasses = () => {
    const count = reviews.length;
    
    if (count === 0) return ""; // No reviews
    if (count === 1) return "max-w-2xl mx-auto"; // Single centered review
    if (count === 2) return "grid grid-cols-1 md:grid-cols-2 gap-6"; // Two reviews
    return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"; // 3+ reviews
  };

  // Navigation for when we have more than 3 reviews on mobile or 6 on desktop
  const needsNavigation = reviews.length > 3;
  
  const nextPage = () => {
    if (testimonialsRef.current) {
      const maxPage = Math.ceil(reviews.length / 3) - 1;
      setCurrentPage(prev => (prev < maxPage ? prev + 1 : 0));
    }
  };
  
  const prevPage = () => {
    if (testimonialsRef.current) {
      const maxPage = Math.ceil(reviews.length / 3) - 1;
      setCurrentPage(prev => (prev > 0 ? prev - 1 : maxPage));
    }
  };

  // Even if there's an error or loading state, ensure we always show at least the initial testimonial
  const visibleReviews = needsNavigation 
    ? reviews.slice(currentPage * 3, (currentPage + 1) * 3)
    : reviews.length > 0 ? reviews : initialTestimonials;

  return (
    <div className="section bg-gray-50">
      <div className="section-inner">
        <SectionHeading 
          title="Trusted by Forward-Thinking Businesses"
          subtitle="We partner with companies of all sizes across New Zealand to implement AI solutions that deliver real results."
          centered
        />
        
        {/* Main testimonials container */}
        <div className="relative max-w-5xl mx-auto">
          {error && (
            <div className="text-yellow-600 mb-4 text-center p-2 bg-yellow-50 rounded-md">
              Couldn't load all reviews. Showing available testimonials.
            </div>
          )}
          
          {/* Testimonials */}
          <div 
            ref={testimonialsRef}
            className={isLoading && reviews.length === 0 ? "animate-pulse" : getLayoutClasses()}
          >
            {isLoading && reviews.length === 0 ? (
              // Loading skeleton
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="glass-card p-6 h-full">
                  <div className="flex mb-6 space-x-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="w-5 h-5 rounded-full bg-gray-200" />
                    ))}
                  </div>
                  <Skeleton className="h-24 mb-4" />
                  <Skeleton className="h-4 w-1/2 mb-2" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              ))
            ) : visibleReviews.length > 0 ? (
              visibleReviews.map((review) => (
                <TestimonialItem
                  key={review.id}
                  text={review.text}
                  author={review.name}
                  position={review.position}
                  company={review.company}
                  rating={review.rating}
                />
              ))
            ) : (
              <div className="col-span-full text-center py-10">
                <p className="text-gray-500">No reviews available at the moment.</p>
              </div>
            )}
          </div>

          {/* Navigation controls - only show when we have enough reviews */}
          {!isLoading && needsNavigation && (
            <div className="flex justify-center mt-8 gap-2">
              <button 
                onClick={prevPage} 
                className="p-2 bg-white rounded-full shadow hover:bg-gray-50 transition-colors"
                aria-label="Previous testimonials"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-navy">
                  <path d="m15 18-6-6 6-6"/>
                </svg>
              </button>
              
              <button 
                onClick={nextPage} 
                className="p-2 bg-white rounded-full shadow hover:bg-gray-50 transition-colors"
                aria-label="Next testimonials"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-navy">
                  <path d="m9 18 6-6-6-6"/>
                </svg>
              </button>
            </div>
          )}
          
          {/* Pagination indicators for mobile */}
          {!isLoading && needsNavigation && (
            <div className="flex justify-center mt-4 gap-1">
              {Array.from({ length: Math.ceil(reviews.length / 3) }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i)}
                  className={`w-2 h-2 rounded-full transition-colors ${currentPage === i ? 'bg-brand-navy' : 'bg-gray-300'}`}
                  aria-label={`Go to page ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrustSection;
